import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { FaPlus } from "react-icons/fa6";
import { getCities, getMenuCards, saveMenuCards } from "../../api/menuCards.js";
import { addToast } from "../../store/app/action.js";
import { PrimaryButton, SecondaryButton } from "../../components/Buttons.jsx";
import InlineLoader from "../../components/InlineLoader.jsx";
import RegionSection from "./RegionSection.jsx";
import {
  uid,
  isValidUrl,
  normalizeCities,
  normalizeRegions,
  serializeRegions,
} from "./constants.js";

const MainMenuCards = () => {
  const dispatch = useDispatch();
  const [regions, setRegions] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const toast = (type, message, timer = 5) =>
    dispatch(addToast({ type, message, timer }));

  // ---- Load -------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const [menuCards, cityList] = await Promise.all([
          getMenuCards(),
          getCities(),
        ]);
        if (cancelled) return;
        setRegions(normalizeRegions(menuCards));
        setCities(normalizeCities(cityList));
      } catch (err) {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Warn on tab close/refresh when there are unsaved changes.
  useEffect(() => {
    if (!dirty) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  // ---- Derived ----------------------------------------------------------
  const cityNameById = useMemo(() => {
    const map = {};
    cities.forEach((c) => (map[c.id] = c.name));
    return map;
  }, [cities]);

  // For a given region, the set of city ids assigned to *other* regions
  // (enforces one-region-per-city in the picker).
  const disabledCityIdsFor = (regionLocalId) => {
    const set = new Set();
    regions.forEach((r) => {
      if (r._localId !== regionLocalId) r.cities.forEach((id) => set.add(id));
    });
    return set;
  };

  // ---- Mutations (all flag dirty) --------------------------------------
  const update = (updater) => {
    setRegions(updater);
    setDirty(true);
  };

  const addRegion = () =>
    update((prev) => [
      ...prev,
      {
        _localId: uid("region"),
        id: null,
        name: "",
        default: false,
        cities: [],
        web: [],
        mobile: [],
      },
    ]);

  const patchRegion = (localId, patch) =>
    update((prev) =>
      prev.map((r) => (r._localId === localId ? { ...r, ...patch } : r))
    );

  // The global default is exclusive: marking one region default clears every
  // other; toggling the current default off leaves no default.
  const setDefaultRegion = (localId) =>
    update((prev) =>
      prev.map((r) => ({
        ...r,
        default: r._localId === localId ? !r.default : false,
      }))
    );

  const deleteRegion = (localId) =>
    update((prev) => prev.filter((r) => r._localId !== localId));

  const addCard = (localId, platform, card) =>
    update((prev) =>
      prev.map((r) =>
        r._localId === localId
          ? { ...r, [platform]: [...r[platform], { _localId: uid("card"), ...card }] }
          : r
      )
    );

  const cardChange = (localId, platform, cardLocalId, patch) =>
    update((prev) =>
      prev.map((r) =>
        r._localId === localId
          ? {
              ...r,
              [platform]: r[platform].map((c) =>
                c._localId === cardLocalId ? { ...c, ...patch } : c
              ),
            }
          : r
      )
    );

  const cardRemove = (localId, platform, cardLocalId) =>
    update((prev) =>
      prev.map((r) =>
        r._localId === localId
          ? { ...r, [platform]: r[platform].filter((c) => c._localId !== cardLocalId) }
          : r
      )
    );

  // ---- Drag & drop ------------------------------------------------------
  const onDragEnd = (result) => {
    const { type, source, destination } = result;
    if (!destination) return;

    if (type === "region") {
      if (source.index === destination.index) return;
      update((prev) => {
        const next = Array.from(prev);
        const [moved] = next.splice(source.index, 1);
        next.splice(destination.index, 0, moved);
        return next;
      });
      return;
    }

    if (type === "card") {
      // Only reorder within the same strip (region + platform).
      if (source.droppableId !== destination.droppableId) return;
      if (source.index === destination.index) return;
      const [regionLocalId, platform] = source.droppableId.split(":");
      update((prev) =>
        prev.map((r) => {
          if (r._localId !== regionLocalId) return r;
          const next = Array.from(r[platform]);
          const [moved] = next.splice(source.index, 1);
          next.splice(destination.index, 0, moved);
          return { ...r, [platform]: next };
        })
      );
    }
  };

  // ---- Save -------------------------------------------------------------
  const handleSave = async () => {
    // Require a global default region whenever regions exist — it's the
    // fallback served when no region matches the user's city.
    if (regions.length > 0 && !regions.some((r) => r.default)) {
      toast(
        "error",
        "Mark one region as the default before saving (used when no city matches).",
        7
      );
      return;
    }
    // Validate optional links before sending. Web is http(s)-only; mobile also
    // accepts saucey:// deep links (converted for iOS on the backend).
    const hasInvalidLink = regions.some(
      (r) =>
        r.web.some((c) => !isValidUrl(c.link)) ||
        r.mobile.some((c) => !isValidUrl(c.link, true))
    );
    if (hasInvalidLink) {
      toast("error", "Some card links are invalid. Fix them before saving.", 7);
      return;
    }
    setSaving(true);
    try {
      const res = await saveMenuCards(serializeRegions(regions));
      // Rehydrate from the response when the BE echoes the saved regions.
      if (Array.isArray(res)) setRegions(normalizeRegions(res));
      else if (Array.isArray(res?.regions)) setRegions(normalizeRegions(res.regions));
      setDirty(false);
      toast("success", "Menu cards saved.");
    } catch (err) {
      toast("error", "Failed to save menu cards. Please try again.", 7);
    } finally {
      setSaving(false);
    }
  };

  // ---- Render -----------------------------------------------------------
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
      {/* Header */}
      <div className="sm:flex sm:justify-between sm:items-center mb-8">
        <div className="mb-4 sm:mb-0">
          <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
            Main Menu Cards
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Manage the city-targeted card carousels served on web &amp; mobile.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SecondaryButton onClick={addRegion} disabled={loading || loadError}>
            <span className="flex items-center gap-1.5">
              <FaPlus className="w-3.5 h-3.5" /> Add region
            </span>
          </SecondaryButton>
          <PrimaryButton
            onClick={handleSave}
            loading={saving}
            disabled={!dirty || loading || loadError}
          >
            Save changes
          </PrimaryButton>
        </div>
      </div>

      {loading ? (
        <InlineLoader message="Loading menu cards…" />
      ) : loadError ? (
        <div className="text-center py-16 text-slate-500 dark:text-slate-400">
          Couldn't load menu cards. Please refresh to try again.
        </div>
      ) : regions.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg">
          <p className="text-slate-500 dark:text-slate-400 mb-4">
            No regions yet. Create one to start adding menu cards.
          </p>
          <PrimaryButton onClick={addRegion}>
            <span className="flex items-center gap-1.5">
              <FaPlus className="w-3.5 h-3.5" /> Add region
            </span>
          </PrimaryButton>
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="regions" type="region">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                className="flex flex-col gap-6"
              >
                {regions.map((region, index) => (
                  <RegionSection
                    key={region._localId}
                    region={region}
                    index={index}
                    cities={cities}
                    cityNameById={cityNameById}
                    disabledCityIds={disabledCityIdsFor(region._localId)}
                    onPatch={(patch) => patchRegion(region._localId, patch)}
                    onSetDefault={() => setDefaultRegion(region._localId)}
                    onDelete={() => deleteRegion(region._localId)}
                    onAddCard={(platform, card) =>
                      addCard(region._localId, platform, card)
                    }
                    onCardChange={(platform, cardLocalId, patch) =>
                      cardChange(region._localId, platform, cardLocalId, patch)
                    }
                    onCardRemove={(platform, cardLocalId) =>
                      cardRemove(region._localId, platform, cardLocalId)
                    }
                  />
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
    </div>
  );
};

export default MainMenuCards;
